import { ActionIcon, Group, Menu } from '@mantine/core';
import { IconCheck, IconChevronDown, IconDroplet, IconMoon, IconSun } from '@tabler/icons-react';
import { CharlieScheme, charlieSchemes, Cmp, useCharlieScheme } from '~portal/lib';
import { touchTarget } from '~portal/styles/touch-target.css';
import { colorSchemeToggle } from './ColorSchemeToggle.css';

const options: Record<CharlieScheme, { label: string; Icon: typeof IconSun }> = {
  light: { label: 'Light', Icon: IconSun },
  dark: { label: 'Dark', Icon: IconMoon },
  blue: { label: 'Blue', Icon: IconDroplet },
};

/**
 * @desc Split theme control: pressing the main button cycles light → dark → blue, the chevron
 * opens the explicit scheme list.
 */
export const ColorSchemeToggle: Cmp = function () {
  const { scheme, setScheme } = useCharlieScheme();

  const active = options[scheme];
  const ActiveIcon = active.Icon;

  const cycle = () => {
    const index = charlieSchemes.indexOf(scheme);
    setScheme(charlieSchemes[(index + 1) % charlieSchemes.length] ?? charlieSchemes[0]);
  };

  return (
    <Group gap={0} wrap="nowrap">
      <ActionIcon
        size="lg"
        variant="default"
        className={`${colorSchemeToggle.button} ${touchTarget}`}
        aria-label={`Colour scheme: ${active.label} — cycle`}
        data-dd-action-name="Cycle colour scheme"
        onClick={cycle}
      >
        <ActiveIcon size={20} />
      </ActionIcon>
      <Menu position="bottom-end" shadow="md" width={160}>
        <Menu.Target>
          <ActionIcon
            size="lg"
            variant="default"
            className={`${colorSchemeToggle.chevron} ${touchTarget}`}
            aria-label="Choose colour scheme"
            data-dd-action-name="Choose colour scheme"
          >
            <IconChevronDown size={14} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>Colour scheme</Menu.Label>
          {charlieSchemes.map((value) => {
            const { label, Icon } = options[value];
            return (
              <Menu.Item
                key={value}
                leftSection={<Icon size={16} />}
                rightSection={value === scheme ? <IconCheck size={14} /> : undefined}
                onClick={() => {
                  setScheme(value);
                }}
              >
                {label}
              </Menu.Item>
            );
          })}
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
};
